import { useEffect, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { haptic } from '../../lib/native'
import { useUiText } from '../../i18n/ui'
import { VoxnelaMark } from './VoxnelaMark'

export interface VoxnelaAlertModalProps {
  open: boolean
  title: string
  message?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  primaryLabel: string
  secondaryLabel?: string
  onPrimary: () => void
  onSecondary?: () => void
  onClose?: () => void
  primaryDisabled?: boolean
  secondaryDisabled?: boolean
  primaryLoading?: boolean
  secondaryLoading?: boolean
  closeOnBackdrop?: boolean
}

export function VoxnelaAlertModal({
  open,
  title,
  message,
  children,
  footer,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
  primaryDisabled = false,
  secondaryDisabled = false,
  primaryLoading = false,
  secondaryLoading = false,
  closeOnBackdrop = true,
}: VoxnelaAlertModalProps) {
  const { text } = useUiText()
  const titleId = useId()
  const close = onClose || onSecondary

  useEffect(() => {
    if (!open || typeof document === 'undefined') return

    const body = document.body
    const html = document.documentElement
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = html.style.overflow
    const previousBodyTouchAction = body.style.touchAction

    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
    body.style.touchAction = 'none'

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || !close) return
      event.preventDefault()
      close()
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      body.style.overflow = previousBodyOverflow
      html.style.overflow = previousHtmlOverflow
      body.style.touchAction = previousBodyTouchAction
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, close])

  if (!open || typeof document === 'undefined') return null

  const runAction = (action: () => void) => {
    void haptic('selection')
    action()
  }

  return createPortal(
    <div
      className="voxnela-alert-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && closeOnBackdrop && close) {
          runAction(close)
        }
      }}
    >
      <section
        className="voxnela-alert-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <span className="voxnela-alert-handle" aria-hidden="true" />

        {close && (
          <button
            type="button"
            className="voxnela-alert-close app-close-button float-island-sheet-close"
            aria-label={text('close')}
            onClick={() => runAction(close)}
          >
            <X size={22} strokeWidth={2} />
          </button>
        )}

        <div className="voxnela-alert-content">
          <h2 id={titleId} className="voxnela-alert-title">{title}</h2>
          <div className="voxnela-alert-divider" aria-hidden="true" />
          {message && <div className="voxnela-alert-message">{message}</div>}
          {children && <div className="voxnela-alert-custom-content">{children}</div>}
        </div>

        <div className="voxnela-alert-footer">
          <div className={`voxnela-alert-actions${secondaryLabel ? '' : ' is-single'}`}>
            {secondaryLabel && onSecondary && (
              <div className="voxnela-alert-action-slot">
                <button
                  type="button"
                  className="voxnela-alert-action voxnela-alert-action--secondary"
                  disabled={secondaryDisabled}
                  aria-busy={secondaryLoading}
                  onClick={() => runAction(onSecondary)}
                >
                  {secondaryLoading && <VoxnelaMark animated />}
                  {secondaryLabel}
                </button>
              </div>
            )}
            <div className="voxnela-alert-action-slot">
              <button
                type="button"
                className="voxnela-alert-action voxnela-alert-action--primary"
                disabled={primaryDisabled}
                aria-busy={primaryLoading}
                onClick={() => runAction(onPrimary)}
              >
                {primaryLoading && <VoxnelaMark animated />}
                {primaryLabel}
              </button>
            </div>
          </div>

          {footer && <div className="voxnela-alert-note">{footer}</div>}
        </div>
      </section>
    </div>,
    document.body,
  )
}
